<template>
  <div class="widget-page">
    <div class="section">
      <div class="header-toggle">
        <h2>{{ $lang.settingsPage_WidgetText }}</h2>
      </div>

      <p class="instructions">
        {{ $lang.settingsPage_WidgetTextInstructions }}
      </p>

      <label class="form-group">
        <div class="form-label">
          {{ $lang.settingsPage_WidgetHeaderMinimized }}
        </div>
        <common-input v-model.trim="form.widgetText[0]"></common-input>
      </label>

      <label class="form-group">
        <div class="form-label">
          {{ $lang.settingsPage_WidgetHeaderExpanded }}
        </div>
        <common-textarea v-model.trim="form.widgetText[1]"></common-textarea>
      </label>

      <label class="form-group">
        <div class="form-label">
          {{ $lang.settingsPage_WidgetSubHeaderExpanded }}
        </div>
        <common-textarea v-model.trim="form.widgetText[2]"></common-textarea>
      </label>
    </div>

    <div class="section">
      <div class="header-toggle">
        <h2>{{ $lang.settingsPage_WidgetOptions }}</h2>
      </div>

      <div class="flex-row">
        <div class="form-group">
          <label class="form-label" for="form.widgetSide">
            {{ $lang.settingsPage_WidgetSide }}
          </label>
          <common-dropdown
            id="form.widgetSide"
            v-model="form.widgetSide"
            :options="widgetSideOptions"
            width="w-32"></common-dropdown>
        </div>

        <label class="form-group">
          <div class="form-label">
            {{ $lang.settingsPage_WidgetDistanceFromSide }}
          </div>
          <common-input v-model.number="form.widgetOffsetX"></common-input>
        </label>

        <label class="form-group">
          <div class="form-label">
            {{ $lang.settingsPage_WidgetDistanceFromBottom }}
          </div>
          <common-input v-model.number="form.widgetOffsetY"></common-input>
        </label>
      </div>
    </div>

    <div class="section">
      <div class="header-toggle">
        <h2>{{ $lang.settingsPage_WidgetCode }}</h2>
      </div>

      <p class="instructions">
        {{ $lang.settingsPage_WidgetInstructions }}
      </p>

      <div class="flex-row">
        <div class="form-group">
          <label class="form-label" for="form.widgetHost">
            {{ $lang.settingsPage_WidgetHost }}</label
          >
          <common-dropdown
            id="form.widgetHost"
            v-model="websiteHost"
            :options="websiteHostOptions"
            width="w-32"></common-dropdown>
        </div>
      </div>

      <template v-if="selectedHost">
        <iframe
          v-if="selectedHost.video"
          :src="selectedHost.video"
          class="widget-video"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen></iframe>

        <p class="instructions">{{ selectedHost.instructions }}</p>

        <div class="widget-code-wrapper">
          <pre class="widget-code">{{ widgetCode }}</pre>
          <common-button class="widget-code-copy-btn" @click="copyToClipboard">
            <mdicon name="clipboard-check" v-if="copied" />
            <span v-else>
              {{ $lang.general_Copy }}
            </span>
          </common-button>
        </div>
      </template>
    </div>

    <common-button variant="primary" class="btn-save" @click="save">
      {{ $lang.general_Save }}
    </common-button>
  </div>
</template>

<script>
  import { SET_BRANDING } from "@/store/actions.type.js";

  export default {
    props: ["branding"],
    data() {
      return {
        form: {
          widgetText: this.branding.widTxt ? [...this.branding.widTxt] : [],
          widgetSide: this.branding.widgetSide ?? "right",
          widgetOffsetX: this.branding.widgetOffsetX ?? 25,
          widgetOffsetY: this.branding.widgetOffsetY ?? 25,
        },
        copied: false,
        websiteHost: "",
        widgetSideOptions: [{ label: "right" }, { label: "left" }],
        websiteHosts: {
          Blackbaud: {
            instructions: this.$lang.settingsPage_WidgetBlackbaudInstructions,
            video:
              "https://www.loom.com/embed/a72d7e8854e44ed5b3b90045f0729854",
          },
          Finalsite: {
            instructions: this.$lang.settingsPage_WidgetFinalsiteInstructions,
          },
          WordPress: {
            instructions: this.$lang.settingsPage_WidgetWordPressInstructions,
            video:
              "https://www.loom.com/embed/00e563f753734744831b73ca0448c98d",
          },
          Squarespace: {
            instructions: this.$lang.settingsPage_WidgetSquarespaceInstructions,
          },
          Wix: {
            instructions: this.$lang.settingsPage_WidgetWixInstructions,
            video:
              "https://www.loom.com/embed/005ce93e6a774d86bfb7145fd69cb765",
          },
          Other: {
            instructions: this.$lang.settingsPage_WidgetOtherInstructions,
          },
        },
      };
    },
    computed: {
      widgetCode() {
        if (this.websiteHost === "WordPress") {
          return `[peerpal school='${this.branding.name}']`;
        }
        /* eslint-disable no-useless-escape */
        return `<script>
  function initPeerPal() {
    peerpal.init("${this.branding.name}");
  }
<\/script>
<script src="https://widget.peerpal.com/js/widget.min.js" defer async onload="initPeerPal()"><\/script>`;
        /* eslint-enable */
      },
      websiteHostOptions() {
        const options = [];

        for (const host in this.websiteHosts) {
          options.push({
            label: host,
          });
        }
        return options;
      },
      selectedHost() {
        return this.websiteHosts[this.websiteHost];
      },
    },
    methods: {
      async save() {
        const response = await this.$store.dispatch(SET_BRANDING, {
          ...this.form,
          widTxt: [...this.form.widgetText],
        });

        if (response) {
          this.$toasted.show(
            this.$lang.settingsPage_SettingsSaved,
            this.$toastedSuccess
          );
        } else {
          this.$toasted.show(
            this.$lang.general_ToastError,
            this.$toastedFailure
          );
        }
      },
      copyToClipboard() {
        navigator.clipboard.writeText(this.widgetCode);
        (this.copied = true), setTimeout(() => (this.copied = false), 5000);
      },
    },
  };
</script>

<style scoped lang="scss">
  .widget-page {
    font-size: 14px;
    color: #757e8c;
    max-width: 680px;
    padding-bottom: 1rem;
  }

  .header-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 1.5rem;

    h2 {
      color: $primary-text;
      font-size: 24px;
      font-weight: 500;
    }
  }

  .instructions {
    margin-bottom: 1.5em;
  }

  .section {
    padding-bottom: 1rem;
  }

  .form-group {
    display: block;
    padding-bottom: 1rem;
  }
  .form-label {
    font-weight: 500;
    padding-bottom: 0.5rem;
    display: block;
  }
  .form-description {
    padding: 1em 0;
  }
  .form-warning {
    display: flex;
    gap: 0.5em;
    align-items: center;
    color: $g-orange;
    font-size: 12px;
  }

  .flex-row {
    display: flex;
    gap: 1rem;

    @media (max-width: $xs) {
      flex-direction: column;
    }
  }

  .widget-code {
    background: $grey-100;
    font-size: 12px;
    width: 100%;
    overflow: auto;
    padding: 1em;
    height: 15em;
  }

  .widget-code-wrapper {
    position: relative;
  }

  .widget-code-copy-btn {
    position: absolute;
    top: 0;
    right: 0;
    width: 80px;
    margin: 1rem;
  }

  .widget-video {
    width: 100%;
    aspect-ratio: 16/10;
    margin-bottom: 1rem;
  }

  .btn-save {
    width: 10rem;
    margin: 2rem 0;
  }
</style>
